<template>
  <div class="home-link-buttons-section">
    <ul class="home-link-buttons-section__list">
      <li class="home-link-buttons-section__item">
        <router-link class="home-link-buttons-section__button" :to="'/'">
          <span class="home-link-buttons-section__button-icon">
            <home-race-icon />
          </span>
          <span class="home-link-buttons-section__button-label">{{
            $tc('HomePage.links.race')
          }}</span>
        </router-link>
      </li>
      <li class="home-link-buttons-section__item">
        <router-link class="home-link-buttons-section__button" :to="'/standings'">
          <span class="home-link-buttons-section__button-icon">
            <home-standings-icon />
          </span>
          <span class="home-link-buttons-section__button-label">{{
            $tc('HomePage.links.standings')
          }}</span>
        </router-link>
      </li>
      <li class="home-link-buttons-section__item">
        <a
          class="home-link-buttons-section__button"
          href="javascript:void(0)"
          @click="onClickAnchorLink(SFBingoUrl)"
        >
          <span class="home-link-buttons-section__button-icon">
            <home-bingo-icon />
          </span>
          <span class="home-link-buttons-section__button-label">{{
            $tc('HomePage.links.bingo')
          }}</span>
        </a>
      </li>
      <li class="home-link-buttons-section__item">
        <router-link class="home-link-buttons-section__button" to="/reward">
          <span class="home-link-buttons-section__button-icon">
            <home-reward-icon />
          </span>
          <span class="home-link-buttons-section__button-label">{{
            $tc('HomePage.links.reward')
          }}</span>
        </router-link>
      </li>
      <li v-for="(icon, i) in displayHomeIcons" :key="i" class="home-link-buttons-section__item">
        <a class="home-link-buttons-section__button" :href="icon.link" target="_blank">
          <span
            class="home-link-buttons-section__button-icon"
            :style="{
              width: icon.width + 'px',
              height: icon.height + 'px',
            }"
          >
            <home-custom-icon :iconPath="icon.iconPath" />
          </span>
          <span class="home-link-buttons-section__button-label">{{ icon.label }}</span>
        </a>
      </li>
    </ul>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, PropType } from '@vue/composition-api'
import dayjs from 'dayjs'
import HomeRaceIcon from '@/components/HomePage/HomePane/parts/icons/HomeRaceIcon.vue'
import HomeStandingsIcon from '@/components/HomePage/HomePane/parts/icons/HomeStandingsIcon.vue'
import HomeBingoIcon from '@/components/HomePage/HomePane/parts/icons/HomeBingoIcon.vue'
import InAppBrowserWrapper, { InAppBrowserResult } from '@/util/inAppBrowser/InAppBrowserWrapper'
import LocalStorageAccessor from '@/util/localstorage/LocalStorageAccessor'
import UserStore from '@/store/stores/pageStore/common/UserStore'
import LoginStore from '@/store/stores/loginStore/LoginStore'
import MessageDialogStore from '@/store/stores/pageStore/common/MessageDialogStore'
import I18n from '@/locales/I18n'
import HomeRewardIcon from '@/components/HomePage/HomePane/parts/icons/HomeRewardIcon.vue'
import HomeCustomIcon from '@/components/HomePage/HomePane/parts/icons/HomeCustomIcon.vue'
import CustomAttributeDataDocument from '@/store/stores/collectionModule/documents/customAttribute/CustomAttributeDataDocument'

export default defineComponent({
  name: 'HomeLinkButtonsSection',
  components: {
    HomeRewardIcon,
    HomeBingoIcon,
    HomeStandingsIcon,
    HomeRaceIcon,
    HomeCustomIcon,
  },
  props: {
    /**
     * Homeアイコンのカスタム属性データ
     */
    homeIcons: {
      type: Array as PropType<CustomAttributeDataDocument[]>,
      default: () => [],
    },
  },
  setup(props) {
    // SF BINGOのURL
    const SFBingoUrl = process.env.VUE_APP_SFBINGO_URL as string

    const now = dayjs().valueOf()
    /**
     * タイルに表示するアイコンを抽出
     */
    const displayHomeIcons = computed(() =>
      props.homeIcons.filter((icon) => {
        if (!icon.displayStartDate && !icon.displayEndDate) {
          // 表示開始日時:なし、表示終了日時:なし の場合、常に表示する
          return true
        }

        if (!icon.displayStartDate && icon.displayEndDate) {
          // 表示開始日時:なし、表示終了日時:あり の場合、現在日時が表示終了日よりも前であれば表示する
          return now < icon.displayEndDate
        }

        if (icon.displayStartDate && !icon.displayEndDate) {
          // 表示開始日時:あり、表示終了日時:なし の場合、現在日時が表示開始日よりも後であれば表示する
          return now > icon.displayStartDate
        }

        if (icon.displayStartDate && icon.displayEndDate) {
          // 表示開始日時:あり、表示終了日時:あり の場合、現在日時が表示開始日と表示終了日の間であれば表示する
          return now >= icon.displayStartDate && now <= icon.displayEndDate
        }

        // 安全のためのフォールバック（理論上起こり得ない）
        return false
      }),
    )

    const inAppBrowser = new InAppBrowserWrapper()

    /**
     * タイルのアンカーリンクがクリックされた場合に呼び出される。
     * @param externalLink 外部サイトのリンク
     */
    const onClickAnchorLink = (externalLink: string) => {
      if (!InAppBrowserWrapper.isAvailable()) {
        // ブラウザの場合、別ウィンドウか別タブで表示する
        const subWindow = window.open(externalLink, '_blank')
        if (subWindow && process.env.VUE_APP_SFBINGO_URL === externalLink) {
          // URLがSFbingoの場合には、APIの認証情報とユーザーIDをローカルストレージに保存する
          LocalStorageAccessor.setCredentialInfo(
            UserStore.value.user.value._organization || '',
            UserStore.value.user.value.id || '',
            LoginStore.value.accessToken || '',
            LoginStore.value.refreshToken || '',
          )
          setTimeout(() => {
            LocalStorageAccessor.clearCredentialInfo()
          }, 60000)
        }

        return
      }

      let injectCode = ''
      if (externalLink === process.env.VUE_APP_SFBINGO_URL) {
        const credentialInfo = {
          organizationId: UserStore.value.user.value._organization,
          userId: UserStore.value.user.value.id,
          accessToken: LoginStore.value.accessToken,
          refreshToken: LoginStore.value.refreshToken,
        }
        const sCredentialInfo = JSON.stringify(credentialInfo)
        injectCode = `window.onLoadCredentials('${sCredentialInfo}');`
      }

      // InAppBrowserでサイトを開く
      inAppBrowser.open(
        externalLink,
        '_blank',
        undefined,
        async (result: InAppBrowserResult) => {
          if (result?.isNetworkError) {
            await MessageDialogStore.value.open({
              title: I18n.t('apiNotification.dialog.0.title') as string,
              message: I18n.t('apiNotification.dialog.0.message') as string,
            })
          } else if (result?.isError) {
            await MessageDialogStore.value.open({
              title: I18n.t('HomePage.errors.homeLinkButtonError.title') as string,
              message: I18n.t('HomePage.errors.homeLinkButtonError.message') as string,
            })
          }
        },
        false,
        injectCode,
      )
    }

    return {
      SFBingoUrl,
      displayHomeIcons,
      onClickAnchorLink,
    }
  },
})
</script>

<style scoped lang="scss">
.home-link-buttons-section {
  padding: 4px 16px;

  &__list {
    display: grid;
    grid-template-columns: repeat(auto-fit, min(calc(33.3% - 4px), 120px));
    gap: 6px;
    justify-content: center;
    width: 100%;
    margin: 0 auto;
  }

  &__button {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 64px;
    background-color: $white;
    border: 1px solid $black10;
    border-radius: 4px;

    &-icon {
      flex-shrink: 0;
      width: 32px;
      height: 32px;
      color: $black70;
    }

    &-label {
      margin-top: 4px;
      font-size: 12px;
      font-weight: 500;
      color: $black70;
      line-height: 1.33;
      letter-spacing: 0.02em;
    }
  }
}
</style>
